import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Save } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function ProfilePage() {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setPhone(user.phone || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const { data } = await api.put("/profile", { name, phone, email, password });
      updateUser(data);
      setPassword("");
      setMessage("Profile updated successfully!");
    } catch (err) {
      setMessage("Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const inputClass = "w-full bg-slate-50 border-b-2 border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <div className="page-with-nav bg-white min-h-screen" data-testid="profile-page">
      <div className="px-4 pt-4">
        <h1 className="text-xl font-black text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Profile
        </h1>
      </div>

      {/* User info */}
      <div className="px-4 py-4 flex items-center gap-4 bg-slate-50 mx-4 mt-3">
        <div className="w-14 h-14 bg-[#0284C7] text-white flex items-center justify-center text-xl font-black">
          {name.charAt(0).toUpperCase()}
        </div>
        <div>
          <p className="font-bold text-slate-900">{name || "User"}</p>
          <p className="text-xs text-slate-500">{email}</p>
          <p className="text-[10px] text-[#0284C7] font-bold uppercase">{user?.role || "user"}</p>
        </div>
      </div>

      {message && (
        <div className={`mx-4 mt-3 px-4 py-2 text-sm font-medium ${
          message.includes("success") ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
        }`}>
          {message}
        </div>
      )}

      <form onSubmit={handleSave} className="p-4 space-y-4" data-testid="profile-form">
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Name</label>
          <input
            data-testid="profile-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Phone</label>
          <input
            data-testid="profile-phone-input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Email</label>
          <input
            data-testid="profile-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">New Password</label>
          <input
            data-testid="profile-password-input"
            type="password"
            placeholder="Leave blank to keep current"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
          />
        </div>
        <button
          data-testid="profile-save-btn"
          type="submit"
          disabled={saving}
          className="w-full bg-[#0284C7] text-white font-bold py-4 text-sm transition-none hover:bg-[#0369A1] flex items-center justify-center gap-2"
        >
          <Save size={16} />
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </form>

      <div className="px-4 pb-6">
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="w-full bg-slate-900 text-white font-bold py-4 text-sm transition-none flex items-center justify-center gap-2"
        >
          <LogOut size={16} />
          Logout
        </button>
      </div>
    </div>
  );
}
